import {
  initVersionedSchema,
  toParagraphContent,
} from '@principle-theorem/editor';
import {
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  IHasSourceIdentifier,
  ITranslationMap,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  ClinicalNote,
  stafferToNamedDoc,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  type IClinicalNote,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithPrimaryKey,
  getError,
  snapshotCombineLatest,
  sortTimestamp,
  toISODate,
  toNamedDocument,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { groupBy } from 'lodash';
import { Observable, combineLatest, defer, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientClinicalNoteDestinationEntity,
  IClinicalNoteJobData,
  IClinicalNoteMigrationData,
} from '../../../destination/entities/patient-clinical-notes';
import { ItemCodeNoteType } from '../../../mappings/item-codes-to-notes-xlsx';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import { PatientNoteSourceEntity } from '../../source/entities/patient-notes';
import {
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PROVIDER_RESOURCE_TYPE } from '../../source/entities/providers';
import { OasisNoteDataTransformer } from '../../source/lib/oasis-notes-data-transformer';
import { OasisItemCodeMappingHandler } from '../mappings/item-codes';
import { OasisPatientNoteCategoryToNoteMappingHandler } from '../mappings/patient-note-categories-to-notes';
import { OasisPatientNoteTypeToNoteMappingHandler } from '../mappings/patient-note-types-to-notes';
import { OasisStafferMappingHandler } from '../mappings/staff';
import { OasisUncategorisedNotes } from './lib/oasis-uncategorised-notes';
import { PatientDestinationEntity } from './patients';

interface IJobData extends IClinicalNoteJobData<OasisPatient> {
  noteCategories: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
  noteTypes: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
  /** Absent on job data persisted before this field existed; those records keep the previous behaviour. */
  exportHasNoteCategories?: boolean;
}

export class PatientClinicalNoteDestinationEntity extends BasePatientClinicalNoteDestinationEntity<
  OasisPatient,
  IJobData
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    patientNote: new PatientNoteSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new OasisStafferMappingHandler(),
    itemCodes: new OasisItemCodeMappingHandler(),
    patientNoteCategories: new OasisPatientNoteCategoryToNoteMappingHandler(),
    noteTypes: new OasisPatientNoteTypeToNoteMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IJobData[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(PROVIDER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const practitioners$ = from(brand$).pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const noteCategories$ =
      this.customMappings.patientNoteCategories.getRecords(translationMap);
    const noteTypes$ = this.customMappings.noteTypes.getRecords(translationMap);
    const exportHasNoteCategories$ = defer(() =>
      OasisUncategorisedNotes.exportHasNoteCategories(migration)
    );

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      sourceItemCodes$,
      noteCategories$,
      noteTypes$,
      exportHasNoteCategories$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            staff,
            practitioners,
            sourceItemCodes,
            noteCategories,
            noteTypes,
            exportHasNoteCategories,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            staff,
            practitioners,
            noteCategories,
            sourcePatient,
            sourceItemCodes,
            destinationEntity,
            noteTypes,
            exportHasNoteCategories,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IJobData
  ): Promise<
    | IClinicalNoteMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients
        .getSourceRecordId(data.sourcePatient.data)
        .toString();
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const clinicalNotes = await this._buildClinicalNotes(migration, data);

      return {
        sourcePatientId,
        patientRef,
        clinicalNotes,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  private async _buildClinicalNotes(
    migration: WithRef<IPracticeMigration>,
    data: IJobData
  ): Promise<(IClinicalNote & IHasSourceIdentifier)[]> {
    const timezone = migration.configuration.timezone;
    const sourcePatientId = data.sourcePatient.data.id;

    const sourcePatientNotes =
      await this.sourceEntities.patientNote.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      );

    const sorted = sourcePatientNotes.sort((treatmentA, treatmentB) =>
      sortTimestamp(
        OasisNoteDataTransformer.getOasisNoteTimestamp(treatmentA, timezone),
        OasisNoteDataTransformer.getOasisNoteTimestamp(treatmentB, timezone)
      )
    );

    const userGroupedNotes = groupBy(
      sorted,
      (record) => record.data.practitionerId ?? '0'
    );

    const clinicalNotes = Object.entries(userGroupedNotes).map(
      ([providerId, userNotes]) => {
        const staffer = OasisNoteDataTransformer.getClinicalNotePractitioner(
          providerId,
          data.staff,
          data.practitioners
        );
        if (!staffer) {
          throw new Error(`Failed to resolve practitioner code: ${providerId}`);
        }

        const flteredNotes = userNotes.filter((note) => {
          if (!note.data.note.trim()) {
            return false;
          }

          const isClinicalNoteType = note.data.noteType === 0;
          if (isClinicalNoteType) {
            return true;
          }

          const categoryId = note.data.categoryId;
          if (!categoryId) {
            return (
              data.exportHasNoteCategories === false &&
              OasisUncategorisedNotes.isClinicalNote(note.data, data.noteTypes)
            );
          }

          const noteCategoryMapping = data.noteCategories.find(
            (category) => category.sourceIdentifier === categoryId.toString()
          );

          if (!noteCategoryMapping?.destinationValue) {
            throw new Error(
              `Note category mapping not found for ${note.data.categoryId}`
            );
          }

          if (
            noteCategoryMapping.destinationValue.value !==
            ItemCodeNoteType.ClinicalNote
          ) {
            return false;
          }

          const noteTypeId = note.data.noteType;
          if (noteTypeId === undefined) {
            return false;
          }
          const noteTypeMapping = data.noteTypes.find(
            (type) => type.sourceIdentifier === noteTypeId.toString()
          );
          if (noteTypeMapping?.destinationValue) {
            return (
              noteTypeMapping.destinationValue.value !==
              ItemCodeNoteType.SterilisationRecord
            );
          }

          return true;
        });

        const dateGroupedNotes = groupBy(flteredNotes, (note) =>
          OasisNoteDataTransformer.getOasisNoteDate(note, timezone)
        );

        return Object.values(dateGroupedNotes).map((notes) => {
          const content = OasisNoteDataTransformer.getClinicalNoteLines(
            notes.map((note) => note.data)
          );

          const noteDate = OasisNoteDataTransformer.getOasisNoteDate(
            notes[0],
            timezone
          );
          const noteTimestamp = OasisNoteDataTransformer.getOasisNoteTimestamp(
            notes[0],
            timezone
          );

          return {
            sourceIdentifier: `${notes[0].data.patientId}-${providerId}-${noteDate}`,
            ...ClinicalNote.init({
              owner: stafferToNamedDoc(staffer),
              content: initVersionedSchema(
                content
                  .map((text) => [
                    toParagraphContent(text.trim() ?? ' '),
                    toParagraphContent(' '),
                  ])
                  .flat()
              ),
              recordDate: noteDate ?? toISODate(toTimestamp()),
              createdAt: noteTimestamp,
              updatedAt: noteTimestamp,
              immutable: true,
            }),
          };
        });
      }
    );
    return clinicalNotes.flat();
  }
}
